import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, FormControl, FormHelperText, Grid, FormControlLabel, Checkbox, TextField, MenuItem, Select, Typography, Paper, IconButton, Button } from '@mui/material';
import { Form, Formik } from 'formik';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { FMTextField } from '../Elements/input';
import useAuth from "../../context/AuthProvider";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { apiClient } from '../../helper/api';
import Swal from "sweetalert2";
import * as Yup from 'yup';

const selectFieldSx = {
  width: '100%',
  '& .MuiSelect-select': { padding: '14.5px 16px' },
  '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' },
};
const textFieldSx = {
  '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px !important' },
  '& .MuiInputBase-input': { padding: '5px 14px !important' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' },
};

interface LicenseesContactInformation {
  partnerLincenseeInformationId: number;
  customerName: string;
  partnersId: number;
  postalAbbvs: string;
  licenseeName: string;
  pageSize : number;
  pageNumber : number;
  cancellationDate: string;
  entity: boolean;
  fein: string;
  licenseNumber: string;
  licenseState: string;
  licensee: string;
  mainLicensee: boolean;
  npn: string;
  title: string;
}

interface Iprops {
  licenseesContactInformation: LicenseesContactInformation,
  handleClose: () => void,
  Open: boolean,
  handleSubmit: () => void
  initValues: LicenseesContactInformation
  fetchLicenseMatrixData: (pageNumber: number, pageSize: number) => Promise<any>;
  pageNumber: number;
  pageSize: number;
  States: { stateId: number, stateName: string, postalAbbv: string }[];
  Partners: { partnersId: number, customerName: string}[];
}

export const AddEditLicenseContact: React.FC<Iprops> = ({
  handleClose, Open, licenseesContactInformation, handleSubmit, initValues, fetchLicenseMatrixData, pageNumber, pageSize, States, Partners
}) => {
  const initialValues: LicenseesContactInformation = licenseesContactInformation ?  
        licenseesContactInformation : initValues;
  const { user } = useAuth();
  const token = user?.token;
  const [SelectedPartner, setSelectedPartner] = React.useState<number>(initialValues.partnersId);

  const requiredString = (message: string) => Yup.string().required(message);
    const validationSchema = Yup.object({
    npn: requiredString('NPN is required'),
    licenseState: requiredString('State is required'),
    partnersId: requiredString('Partner is required'),
    licensee: requiredString('Licensee is required'),
    licenseNumber: requiredString('License Number is required'),
    mainLicensee: requiredString('Main Licensee is required'),
    entity: requiredString('Entity is required'),
  });

  async function saveLicensee(data: any) {
    const url = '/LicenseMatrix/AddLicensee';
    const requestData = {
      Licensee: data.licensee,
      LicenseState: data.licenseState,
      LicenseNumber: data.licenseNumber,
      Title: data.title,
      PartnersId: SelectedPartner,
      MainLicensee: data.mainLicensee,
      Entity: data.entity,
      Npn: data.npn,
      Fein: data.fein,
      CancellationDate: data.cancellationDate
    };

    try {
      Swal.fire({
        title: 'Saving...',
        text: 'Please wait while we save your data.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      }).then(() => {
        fetchLicenseMatrixData(pageNumber,pageSize);
      });
      const response = await apiClient(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });

      if (response.resp === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Data added successfully.',
          confirmButtonText: 'OK'
        });
      } else if (response.resp === 1) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: response.message || 'Failed to add data.',
          confirmButtonText: 'OK'
        });
      } else if (response.resp === 2) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Unexpected response from the server.',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'An unexpected error occurred. Please try again.',
        confirmButtonText: 'OK'
      });
    }
  };

  const updateLicenseesContactInformation = async (updatedData: any) => {
    try {
      Swal.fire({
        title: 'Updating...',
        text: 'Please wait while we update your data.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await apiClient(`/LicenseMatrix/UpdateLicensee`, {
        method: "POST",
        body: JSON.stringify(updatedData),
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        })
      });

      if (response.resp === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Data updated successfully.',
          confirmButtonText: 'OK'
        }).then(() => {
          fetchLicenseMatrixData(pageNumber,pageSize);
        });
      } else if (response.resp === 1) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: response.message || 'Failed to add data.',
          confirmButtonText: 'OK'
        });
      } else if (response.resp === 2) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Unexpected response from the server.',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'An unexpected error occurred. Please try again.',
        confirmButtonText: 'OK'
      });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={Open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            width: '90%',
            maxWidth: 900,
            minHeight: '50vh',
            maxHeight: '90vh',
            borderRadius: 2,
            boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          },
        }}
        sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            py: 2,
            px: 3,
            background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <BadgeOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
            <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
              Licensee Contact
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
            aria-label="Close dialog"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(data, { setSubmitting }) => {
            if (data.partnerLincenseeInformationId === 0) {
              saveLicensee(data);
            } else {
              updateLicenseesContactInformation([{ ...data, partnersId: (SelectedPartner === 0 ? data.partnersId : SelectedPartner) }]);
              setSubmitting(false);
            }
            setSelectedPartner(0);
            handleSubmit();
          }}
        >
          {({ values, isSubmitting, errors, setFieldValue }) => (
            <Form className='overflow-auto'>
              <DialogContent sx={{ p: '0px' , '& > .MuiGrid-root': { px: { xs: 2, sm: 3 }, py: 3 } }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                      Licensee details
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', backgroundColor: 'grey.50' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.licenseState} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">State</FormHelperText>
                            <Select
                              value={values.licenseState}
                              id="test-select-label"
                              label={null}
                              onChange={(e) => setFieldValue("licenseState", e.target.value)}
                              name="licenseState"
                              IconComponent={ExpandMoreIcon}
                              displayEmpty
                              sx={selectFieldSx}
                              renderValue={(selected) => {
                                const selectedState = States.find((state) => state.postalAbbv === selected);
                                return selectedState ? selectedState.stateName : "Select State";
                              }}
                            >
                              <MenuItem key={0} value={0}>Select State</MenuItem>
                              {States.map((state, key) => (
                                <MenuItem key={key} value={state.postalAbbv}>{state.stateName}</MenuItem>
                              ))}
                            </Select>
                            <FormHelperText error>{errors.licenseState}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.partnersId} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">Client</FormHelperText>
                            <Select
                              value={SelectedPartner === 0 ? values.partnersId : SelectedPartner}
                              id="test-select-label-client"
                              onChange={(e) => {
                                setFieldValue("customerName", Partners.find((item) => item.partnersId === e.target.value)?.customerName);
                                setSelectedPartner(Number(e.target.value));
                              }}
                              name="partnersId"
                              IconComponent={ExpandMoreIcon}
                              displayEmpty
                              sx={selectFieldSx}
                              renderValue={(selected) => {
                                const selectedPartner = Partners.find((partner) => partner.partnersId === selected);
                                return selectedPartner ? selectedPartner.customerName : "Select Client";
                              }}
                            >
                              <MenuItem key={0} value={0}>Select Client</MenuItem>
                              {Partners.map((partner, key) => (
                                <MenuItem key={key} value={partner.partnersId}>{partner.customerName}</MenuItem>
                              ))}
                            </Select>
                            <FormHelperText error>{errors.partnersId}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.cancellationDate} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">Cancellation Date</FormHelperText>
                            <DatePicker
                              inputFormat="MM/DD/YYYY"
                              value={(values.cancellationDate === '') ? new Date(values.cancellationDate) : values.cancellationDate}
                              onChange={(v) => {
                                if (v) {
                                  let formattedDate = '';
                                  try {
                                    formattedDate = new Date(v).toISOString().split('.')[0];
                                  } catch {
                                    formattedDate = (v as any).toLocaleString();
                                  }
                                  initialValues.cancellationDate = formattedDate;
                                  setFieldValue("cancellationDate", formattedDate);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  fullWidth
                                  size="small"
                                  {...params}
                                  id="cancellationDate"
                                  name="cancellationDate"
                                  sx={{...textFieldSx, '& .MuiInputBase-input': { padding: '14.5px 14px !important' }}}
                                />
                              )}
                            />
                            {errors.cancellationDate && <FormHelperText error>{errors.cancellationDate}</FormHelperText>}
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.licensee} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">Licensee</FormHelperText>
                            <FMTextField
                              label={null}
                              name="licensee"
                              value={initialValues.licensee}
                              onChange={(e: any) => {
                                initialValues.licensee = e.target.value;
                                setFieldValue("licensee", e.target.value);
                              }}
                              fullWidth
                              variant="outlined"
                              sx={{...textFieldSx, '& .MuiInputBase-input': { padding: '14.5px 14px !important' }}}
                            />
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.licenseNumber} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">License Number</FormHelperText>
                            <FMTextField
                              label={null}
                              name="licenseNumber"
                              value={initialValues.licenseNumber}
                              onChange={(e: any) => {
                                initialValues.licenseNumber = e.target.value;
                                setFieldValue("licenseNumber", e.target.value);
                              }}
                              fullWidth
                              variant="outlined"
                              sx={{...textFieldSx, '& .MuiInputBase-input': { padding: '14.5px 14px !important' }}}
                            />
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.npn} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">NPN</FormHelperText>
                            <FMTextField
                              label={null}
                              name="npn"
                              value={initialValues.npn}
                              onChange={(e: any) => {
                                initialValues.npn = e.target.value;
                                setFieldValue("npn", e.target.value);
                              }}
                              fullWidth
                              variant="outlined"
                              sx={{...textFieldSx, '& .MuiInputBase-input': { padding: '14.5px 14px !important' }}}
                            />
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.fein} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">FEIN</FormHelperText>
                            <FMTextField
                              label={null}
                              name="fein"
                              value={initialValues.fein}
                              onChange={(e: any) => {
                                initialValues.fein = e.target.value;
                                setFieldValue("fein", e.target.value);
                              }}
                              fullWidth
                              variant="outlined"
                              sx={{...textFieldSx, '& .MuiInputBase-input': { padding: '14.5px 14px !important' }}}
                            />
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Grid container spacing={2} sx={{ marginTop: 2 }}>
                            <Grid item xs={12} sm={6}>
                              <FormControl sx={{ width: '100%' }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={initialValues.mainLicensee}
                                      onChange={(e) => {
                                        initialValues.mainLicensee = e.target.checked;
                                        setFieldValue("mainLicensee", e.target.checked);
                                      }}
                                      name="mainLicense"
                                      color="primary"
                                    />
                                  }
                                  label="Main License"
                                />
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <FormControl sx={{ width: '100%' }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={initialValues.entity}
                                      onChange={(e) => {
                                        initialValues.entity = e.target.checked;
                                        setFieldValue("entity", e.target.checked);
                                      }}
                                      name="entity"
                                      color="primary"
                                    />
                                  }
                                  label="Entity"
                                />
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions
                sx={{
                  px: 3,
                  py: 2,
                  gap: 1.5,
                  borderTop: 1,
                  borderColor: 'divider',
                  backgroundColor: 'grey.50',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={handleClose}
                  startIcon={<CloseOutlinedIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#e2e8f0',
                    color: '#475569',
                    '&:hover': {
                      borderColor: '#2099d2',
                      backgroundColor: '#2099d214',
                      color: '#2099d2',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="medium"
                  type="submit"
                  disabled={isSubmitting}
                  startIcon={<SaveOutlinedIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#2099d2',
                    '&:hover': { backgroundColor: '#176990' },
                    px: 2.5,
                  }}
                >
                  Submit
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </LocalizationProvider>
  );
}
